import React, { useEffect, useState, useRef } from 'react'
import orderBy from 'lodash/orderBy'
import Modal from 'react-modal'
import type { ComponentType } from 'react'

import ModalTitle from './modal/ModalTitle'
import ModalInner from './modal/ModalInner'
import ModalListWrapper from './modal/ModalListWrapper'
import ModalHeader from './modal/ModalHeader'
import ModalFooter from './modal/ModalFooter'
import ModalCloseButton from './modal/ModalCloseButton'
import modalStyles from './modal/modalStyles'

import PanelButtonPopupIcon from './PanelButtonPopupIcon'
import AreaPanelButton from './areaSelectModal/AreaPanelButton'
import LineCompanyList from './stationSelectModal/LineCompanyList'
import SearchControlBox from '@pc/components/Works/Panel/shared/SearchControlBox'
import useWorksCount from '@pc/components/Works/Panel/hook/useWorksCount'
import { buildDynamicSearchApiUrl } from '@pc/components/Works/Panel/util/workSearchApi'
import type { SearchParamsAttributes, Prefecture, RawLineCompany, Line, Station } from '@pc/components/Works/Panel/type'

const ReactModal = Modal as unknown as ComponentType<any>

interface PropsType {
  params: SearchParamsAttributes
  prefecture: Prefecture
}

type MappedLines = Record<string, Line[]>

const GUIDE_AREA_URL = '/guide_area.htm'

const isKey = <T extends object>(x: T, k: PropertyKey): k is keyof T => {
  return k in x
}

const el = document.querySelector<HTMLElement>('.js-works-search-panel')
if (el !== null) {
  Modal.setAppElement('.js-works-search-panel')
}

const StationSelectModal = (props: PropsType) => {
  const rawLineCompaniesAttr = el?.dataset.worksLineCompanies
  const linesAttr = el?.dataset.worksLines
  const stationsAttr = el?.dataset.worksStations

  if (rawLineCompaniesAttr === undefined) return
  if (linesAttr === undefined) return
  if (stationsAttr === undefined) return

  const { params, prefecture } = props

  const buildInitialStationState = () => {
    const rawLineCompanies: RawLineCompany[] = JSON.parse(rawLineCompaniesAttr).data
    const lines: Line[] = JSON.parse(linesAttr).data
    const stations: Station[] = JSON.parse(stationsAttr).data

    const mappedLines: MappedLines = rawLineCompanies.reduce((result, lineCompany) => {
      const value = lines.filter((line) => line.attributes.line_company_id === lineCompany.attributes.id)
      return { ...result, [lineCompany.id]: value }
    }, {})

    const mappedStations = lines.reduce((result, line) => {
      const value = stations.filter((station) => {
        const lineIds = station.relationships.lines.data.map((stationLine) => stationLine.id)
        return lineIds.includes(line.id)
      })
      return { ...result, [line.id]: value }
    }, {})

    // マイナー路線しか持たない鉄道会社が上の方に来てしまうことを防ぐために鉄道会社を路線数が多い順に並べかえている
    const lineCompanyItems = (lineCompanyId: string) => {
      return isKey(mappedLines, lineCompanyId) ? mappedLines[lineCompanyId] : []
    }
    const lineCompanies = orderBy(rawLineCompanies, [(lineCompany) => lineCompanyItems(lineCompany.id).length], 'desc')

    return {
      lineCompanies,
      mappedLines,
      mappedStations
    }
  }

  const { lineCompanies, mappedLines, mappedStations } = buildInitialStationState()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([])
  const { worksCount, updateCountFromParams } = useWorksCount()
  const renderFlgRef = useRef(false)

  const toggleModal = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setModalIsOpen(!modalIsOpen)
  }

  const changeStationIds = (stationIds: string[]) => {
    setSelectedStationIds(stationIds)
  }

  const clearState = () => {
    setSelectedStationIds([])
  }

  const mergedParams = () => {
    // 駅・路線系の検索の場合は地域系の条件をリセットする仕様になっている
    // 検索パネルの仕様上、lineを変更することができず情報を保持していると検索結果がおかしくなる可能性があるためリセットする
    return { ...params, station_ids: selectedStationIds, lines_ids: [], area_codes: [], city_ids: [], town_ids: [] }
  }

  useEffect(() => {
    if (renderFlgRef.current) {
      updateCountFromParams(mergedParams())
    } else {
      renderFlgRef.current = true
    }
  }, [selectedStationIds])

  useEffect(() => {
    const preSelectedStationIds: string[] = params.station_ids ?? []
    setSelectedStationIds(preSelectedStationIds)
  }, [])

  return (
    <>
      <AreaPanelButton onClick={toggleModal}>
        路線・駅変更
        <PanelButtonPopupIcon />
      </AreaPanelButton>
      <ReactModal isOpen={modalIsOpen} onRequestClose={toggleModal} style={modalStyles}>
        <ModalInner>
          <ModalCloseButton onClick={toggleModal} />
          <ModalHeader>
            <ModalTitle>{prefecture.attributes.name}内の路線・駅変更</ModalTitle>
            <a href={GUIDE_AREA_URL}>都道府県を変更</a>
          </ModalHeader>
          <ModalListWrapper>
            <LineCompanyList
              lineCompanies={lineCompanies}
              mappedLines={mappedLines}
              mappedStations={mappedStations}
              selectedStationIds={selectedStationIds}
              changeStationIds={changeStationIds}
            />
          </ModalListWrapper>
          <ModalFooter>
            <SearchControlBox searchUrl={buildDynamicSearchApiUrl(mergedParams())} onClickClearButton={clearState} worksCount={worksCount} />
          </ModalFooter>
        </ModalInner>
      </ReactModal>
    </>
  )
}

export default StationSelectModal
